import { ApiResult } from "./api_result";
import HttpError from "./http_error";
import i18next from "i18next";

/**
 * Unwraps an ApiResult into its data or throws a structured error.
 * Errors thrown here are caught by TanStack Query and handled globally
 * via QueryCache/MutationCache onError.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function unwrapResponse<T>(
  response: ApiResult<T>,
  entityName: string,
): T {
  if (!response.isSuccessfull()) {
    if (response.isCustomError()) {
      throw response.getCustomError();
    }

    if (response.isServerError()) {
      throw new HttpError(errorMessages.serverError(), response.getErrorCode());
    }

    if (response.isBadRequest()) {
      throw new HttpError(errorMessages.badRequest(), response.getErrorCode());
    }

    if (response.isConflict()) {
      throw new HttpError(
        errorMessages.dataExists(entityName),
        response.getErrorCode(),
      );
    }

    if (response.isTimeout()) {
      throw new HttpError(errorMessages.timeout(), response.getErrorCode());
    }

    if (response.isUnprocessableEntity()) {
      throw new HttpError(errorMessages.badRequest(), response.getErrorCode());
    }

    if (response.isNotFound()) {
      throw new HttpError(errorMessages.notFound(), response.getErrorCode());
    }

    if (response.isForbidden()) {
      throw new HttpError(errorMessages.forbidden(), response.getErrorCode());
    }

    if (response.isUnauthorized()) {
      throw new HttpError(
        errorMessages.unauthorized(),
        response.getErrorCode(),
      );
    }

    throw new HttpError(
      errorMessages.errorResponse(response.getErrorCode()),
      response.getErrorCode(),
    );
  }

  return response.getData();
}

const errorMessages = {
  errorResponse: (statusCode: string) =>
    i18next.t("ERROR:HTTP_Error")?.replace("${statusCode}", statusCode),
  serverError: () => i18next.t("ERROR:HTTP_500"),
  badRequest: () => i18next.t("ERROR:HTTP_400"),
  unauthorized: () => i18next.t("ERROR:HTTP_401"),
  forbidden: () => i18next.t("ERROR:HTTP_403"),
  notFound: () => i18next.t("ERROR:HTTP_404"),
  timeout: () => i18next.t("ERROR:HTTP_Timeout"),
  dataExists: (entity: string) =>
    i18next.t("ERROR:HTTP_409")?.replace("${entity}", entity),
};
